/**
 * Collectible Placement Configuration
 *
 * Defines the positions of all collectible letters (A-Z) and numbers (1-9)
 * for each world. Each world has 35 collectibles distributed across the
 * inter-cave segments.
 *
 * Placement strategy:
 * - 35 items per world: 26 letters + 9 numbers
 * - Distributed across 5 segments (before cave 1, between caves 1-2, 2-3, 3-4, 4-5, after cave 5)
 * - Approximately 7 items per segment
 * - Positioned on or above reachable platforms
 * - Y positions vary: some at ground level, some requiring jumps
 *
 * Ground level: GROUND_Y = 656 (720 - 64)
 * Collectibles float above platforms at various heights.
 *
 * Requirements: 7.1, 7.2
 */

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface CollectiblePlacement {
  /** Unique identifier, e.g. "letter-A" or "number-3" */
  id: string;
  /** World x position in pixels */
  x: number;
  /** World y position in pixels (on or above platform) */
  y: number;
}

/**
 * Maps world ID to its array of collectible placements.
 * Each world has exactly 35 placements: 26 letters (A-Z) + 9 numbers (1-9).
 */
export type WorldCollectibleConfig = Record<string, CollectiblePlacement[]>;

// ─── Constants ───────────────────────────────────────────────────────────────

/** Ground Y position (top of ground tile) */
const GROUND_Y = 656;

/** Heights above ground for collectible placement variety */
const HEIGHT = {
  /** On ground level (floating just above ground) */
  ground: GROUND_Y - 40,
  /** On a low step/bump */
  step: GROUND_Y - 70,
  /** Low platform height */
  low: GROUND_Y - 110,
  /** Between platforms (requires running jump) */
  midLow: GROUND_Y - 140,
  /** Medium platform height (requires small jump) */
  medium: GROUND_Y - 175,
  /** Above medium (requires precise jump) */
  midHigh: GROUND_Y - 210,
  /** High platform height (requires full jump) */
  high: GROUND_Y - 250,
  /** Very high (requires jump from elevated platform) */
  veryHigh: GROUND_Y - 300,
} as const;


// ─── Floresta Encantada ──────────────────────────────────────────────────────
// Cave triggers at: x=2500, 7300, 12800, 17700, 22600
// Segments: [start→2500], [2500→7300], [7300→12800], [12800→17700], [17700→22600+]

const florestaEncantadaPlacements: CollectiblePlacement[] = [
  // Segment 1: start to cave 1 (x: 400 - 2300) — 7 items
  { id: 'letter-A', x: 500, y: HEIGHT.ground },
  { id: 'letter-B', x: 750, y: HEIGHT.medium },
  { id: 'letter-C', x: 1000, y: HEIGHT.step },
  { id: 'letter-D', x: 1300, y: HEIGHT.high },
  { id: 'letter-E', x: 1600, y: HEIGHT.ground },
  { id: 'letter-F', x: 1900, y: HEIGHT.midLow },
  { id: 'letter-G', x: 2200, y: HEIGHT.low },

  // Segment 2: cave 1 to cave 2 (x: 2700 - 7100) — 7 items
  { id: 'letter-H', x: 3000, y: HEIGHT.step },
  { id: 'letter-I', x: 3500, y: HEIGHT.high },
  { id: 'letter-J', x: 4100, y: HEIGHT.ground },
  { id: 'letter-K', x: 4700, y: HEIGHT.midHigh },
  { id: 'letter-L', x: 5300, y: HEIGHT.veryHigh },
  { id: 'letter-M', x: 5900, y: HEIGHT.ground },
  { id: 'letter-N', x: 6600, y: HEIGHT.medium },

  // Segment 3: cave 2 to cave 3 (x: 7500 - 12600) — 7 items
  { id: 'letter-O', x: 7800, y: HEIGHT.low },
  { id: 'letter-P', x: 8500, y: HEIGHT.high },
  { id: 'letter-Q', x: 9200, y: HEIGHT.ground },
  { id: 'letter-R', x: 9900, y: HEIGHT.veryHigh },
  { id: 'letter-S', x: 10600, y: HEIGHT.step },
  { id: 'letter-T', x: 11300, y: HEIGHT.midLow },
  { id: 'letter-U', x: 12000, y: HEIGHT.high },

  // Segment 4: cave 3 to cave 4 (x: 13000 - 17500) — 7 items
  { id: 'letter-V', x: 13200, y: HEIGHT.ground },
  { id: 'letter-W', x: 13900, y: HEIGHT.midHigh },
  { id: 'letter-X', x: 14600, y: HEIGHT.veryHigh },
  { id: 'letter-Y', x: 15300, y: HEIGHT.step },
  { id: 'letter-Z', x: 16000, y: HEIGHT.medium },
  { id: 'number-1', x: 16700, y: HEIGHT.ground },
  { id: 'number-2', x: 17400, y: HEIGHT.low },

  // Segment 5: cave 4 to end (x: 17900 - 24500) — 7 items
  { id: 'number-3', x: 18200, y: HEIGHT.high },
  { id: 'number-4', x: 19000, y: HEIGHT.ground },
  { id: 'number-5', x: 19800, y: HEIGHT.midLow },
  { id: 'number-6', x: 20600, y: HEIGHT.veryHigh },
  { id: 'number-7', x: 21400, y: HEIGHT.step },
  { id: 'number-8', x: 22000, y: HEIGHT.medium },
  { id: 'number-9', x: 23200, y: HEIGHT.ground },
];

// ─── Montanha Gelada ─────────────────────────────────────────────────────────
// Cave triggers at: x=3700, 8750, 12950, 17850, 22700
// Segments: [start→3700], [3700→8750], [8750→12950], [12950→17850], [17850→24500]

const montanhaGeladaPlacements: CollectiblePlacement[] = [
  // Segment 1: start to cave 1 (x: 400 - 3500) — 7 items
  { id: 'letter-A', x: 600, y: HEIGHT.ground },
  { id: 'letter-B', x: 1000, y: HEIGHT.medium },
  { id: 'letter-C', x: 1450, y: HEIGHT.ground },
  { id: 'letter-D', x: 1900, y: HEIGHT.high },
  { id: 'letter-E', x: 2350, y: HEIGHT.low },
  { id: 'letter-F', x: 2800, y: HEIGHT.ground },
  { id: 'letter-G', x: 3300, y: HEIGHT.medium },

  // Segment 2: cave 1 to cave 2 (x: 3900 - 8550) — 7 items
  { id: 'letter-H', x: 4200, y: HEIGHT.ground },
  { id: 'letter-I', x: 4900, y: HEIGHT.high },
  { id: 'letter-J', x: 5600, y: HEIGHT.low },
  { id: 'letter-K', x: 6200, y: HEIGHT.ground },
  { id: 'letter-L', x: 6800, y: HEIGHT.medium },
  { id: 'letter-M', x: 7500, y: HEIGHT.ground },
  { id: 'letter-N', x: 8200, y: HEIGHT.low },

  // Segment 3: cave 2 to cave 3 (x: 8950 - 12750) — 7 items
  { id: 'letter-O', x: 9200, y: HEIGHT.ground },
  { id: 'letter-P', x: 9700, y: HEIGHT.medium },
  { id: 'letter-Q', x: 10200, y: HEIGHT.high },
  { id: 'letter-R', x: 10700, y: HEIGHT.ground },
  { id: 'letter-S', x: 11200, y: HEIGHT.low },
  { id: 'letter-T', x: 11700, y: HEIGHT.ground },
  { id: 'letter-U', x: 12300, y: HEIGHT.medium },

  // Segment 4: cave 3 to cave 4 (x: 13150 - 17650) — 7 items
  { id: 'letter-V', x: 13400, y: HEIGHT.ground },
  { id: 'letter-W', x: 14100, y: HEIGHT.high },
  { id: 'letter-X', x: 14800, y: HEIGHT.low },
  { id: 'letter-Y', x: 15500, y: HEIGHT.ground },
  { id: 'letter-Z', x: 16200, y: HEIGHT.medium },
  { id: 'number-1', x: 16900, y: HEIGHT.ground },
  { id: 'number-2', x: 17500, y: HEIGHT.low },

  // Segment 5: cave 4 to end (x: 18050 - 24500) — 7 items
  { id: 'number-3', x: 18400, y: HEIGHT.ground },
  { id: 'number-4', x: 19200, y: HEIGHT.high },
  { id: 'number-5', x: 20000, y: HEIGHT.medium },
  { id: 'number-6', x: 20800, y: HEIGHT.ground },
  { id: 'number-7', x: 21600, y: HEIGHT.low },
  { id: 'number-8', x: 22200, y: HEIGHT.ground },
  { id: 'number-9', x: 23500, y: HEIGHT.medium },
];

// ─── Reino Encantado ─────────────────────────────────────────────────────────
// Cave triggers at: x=3600, 7900, 12850, 17750, 22650
// Segments: [start→3600], [3600→7900], [7900→12850], [12850→17750], [17750→24500]

const reinoEncantadoPlacements: CollectiblePlacement[] = [
  // Segment 1: start to cave 1 (x: 400 - 3400) — 7 items
  { id: 'letter-A', x: 550, y: HEIGHT.ground },
  { id: 'letter-B', x: 950, y: HEIGHT.low },
  { id: 'letter-C', x: 1400, y: HEIGHT.medium },
  { id: 'letter-D', x: 1850, y: HEIGHT.ground },
  { id: 'letter-E', x: 2300, y: HEIGHT.high },
  { id: 'letter-F', x: 2750, y: HEIGHT.ground },
  { id: 'letter-G', x: 3200, y: HEIGHT.low },

  // Segment 2: cave 1 to cave 2 (x: 3800 - 7700) — 7 items
  { id: 'letter-H', x: 4100, y: HEIGHT.ground },
  { id: 'letter-I', x: 4650, y: HEIGHT.medium },
  { id: 'letter-J', x: 5200, y: HEIGHT.ground },
  { id: 'letter-K', x: 5750, y: HEIGHT.high },
  { id: 'letter-L', x: 6300, y: HEIGHT.low },
  { id: 'letter-M', x: 6850, y: HEIGHT.ground },
  { id: 'letter-N', x: 7400, y: HEIGHT.medium },

  // Segment 3: cave 2 to cave 3 (x: 8100 - 12650) — 7 items
  { id: 'letter-O', x: 8400, y: HEIGHT.ground },
  { id: 'letter-P', x: 9100, y: HEIGHT.low },
  { id: 'letter-Q', x: 9800, y: HEIGHT.high },
  { id: 'letter-R', x: 10500, y: HEIGHT.ground },
  { id: 'letter-S', x: 11200, y: HEIGHT.medium },
  { id: 'letter-T', x: 11800, y: HEIGHT.ground },
  { id: 'letter-U', x: 12400, y: HEIGHT.low },

  // Segment 4: cave 3 to cave 4 (x: 13050 - 17550) — 7 items
  { id: 'letter-V', x: 13300, y: HEIGHT.ground },
  { id: 'letter-W', x: 14000, y: HEIGHT.medium },
  { id: 'letter-X', x: 14700, y: HEIGHT.high },
  { id: 'letter-Y', x: 15400, y: HEIGHT.ground },
  { id: 'letter-Z', x: 16100, y: HEIGHT.low },
  { id: 'number-1', x: 16800, y: HEIGHT.ground },
  { id: 'number-2', x: 17400, y: HEIGHT.medium },

  // Segment 5: cave 4 to end (x: 17950 - 24500) — 7 items
  { id: 'number-3', x: 18300, y: HEIGHT.ground },
  { id: 'number-4', x: 19100, y: HEIGHT.low },
  { id: 'number-5', x: 19900, y: HEIGHT.high },
  { id: 'number-6', x: 20700, y: HEIGHT.ground },
  { id: 'number-7', x: 21500, y: HEIGHT.medium },
  { id: 'number-8', x: 22100, y: HEIGHT.ground },
  { id: 'number-9', x: 23400, y: HEIGHT.low },
];

// ─── Espaço Sideral ──────────────────────────────────────────────────────────
// Cave triggers at: x=3200, 7550, 12450, 17350, 22200
// Segments: [start→3200], [3200→7550], [7550→12450], [12450→17350], [17350→24500]

const espacoSideralPlacements: CollectiblePlacement[] = [
  // Segment 1: start to cave 1 (x: 400 - 3000) — 7 items
  { id: 'letter-A', x: 500, y: HEIGHT.ground },
  { id: 'letter-B', x: 880, y: HEIGHT.medium },
  { id: 'letter-C', x: 1260, y: HEIGHT.ground },
  { id: 'letter-D', x: 1640, y: HEIGHT.high },
  { id: 'letter-E', x: 2020, y: HEIGHT.low },
  { id: 'letter-F', x: 2400, y: HEIGHT.ground },
  { id: 'letter-G', x: 2800, y: HEIGHT.medium },

  // Segment 2: cave 1 to cave 2 (x: 3400 - 7350) — 7 items
  { id: 'letter-H', x: 3700, y: HEIGHT.ground },
  { id: 'letter-I', x: 4300, y: HEIGHT.high },
  { id: 'letter-J', x: 4900, y: HEIGHT.ground },
  { id: 'letter-K', x: 5500, y: HEIGHT.low },
  { id: 'letter-L', x: 6100, y: HEIGHT.medium },
  { id: 'letter-M', x: 6700, y: HEIGHT.ground },
  { id: 'letter-N', x: 7200, y: HEIGHT.low },

  // Segment 3: cave 2 to cave 3 (x: 7750 - 12250) — 7 items
  { id: 'letter-O', x: 8000, y: HEIGHT.ground },
  { id: 'letter-P', x: 8650, y: HEIGHT.medium },
  { id: 'letter-Q', x: 9300, y: HEIGHT.high },
  { id: 'letter-R', x: 9950, y: HEIGHT.ground },
  { id: 'letter-S', x: 10600, y: HEIGHT.low },
  { id: 'letter-T', x: 11250, y: HEIGHT.ground },
  { id: 'letter-U', x: 11900, y: HEIGHT.medium },

  // Segment 4: cave 3 to cave 4 (x: 12650 - 17150) — 7 items
  { id: 'letter-V', x: 12900, y: HEIGHT.ground },
  { id: 'letter-W', x: 13550, y: HEIGHT.low },
  { id: 'letter-X', x: 14200, y: HEIGHT.high },
  { id: 'letter-Y', x: 14850, y: HEIGHT.ground },
  { id: 'letter-Z', x: 15500, y: HEIGHT.medium },
  { id: 'number-1', x: 16150, y: HEIGHT.ground },
  { id: 'number-2', x: 16800, y: HEIGHT.low },

  // Segment 5: cave 4 to end (x: 17550 - 24500) — 7 items
  { id: 'number-3', x: 18000, y: HEIGHT.ground },
  { id: 'number-4', x: 18900, y: HEIGHT.high },
  { id: 'number-5', x: 19800, y: HEIGHT.medium },
  { id: 'number-6', x: 20700, y: HEIGHT.ground },
  { id: 'number-7', x: 21600, y: HEIGHT.low },
  { id: 'number-8', x: 22100, y: HEIGHT.ground },
  { id: 'number-9', x: 23600, y: HEIGHT.medium },
];

// ─── Exported Configuration ──────────────────────────────────────────────────

/**
 * World collectible placements configuration.
 * Maps each world ID to its 35 collectible placements (26 letters + 9 numbers).
 */
export const WORLD_COLLECTIBLE_PLACEMENTS: WorldCollectibleConfig = {
  'floresta-encantada': florestaEncantadaPlacements,
  'montanha-gelada': montanhaGeladaPlacements,
  'reino-encantado': reinoEncantadoPlacements,
  'espaco-sideral': espacoSideralPlacements,
};

/**
 * Returns the collectible placements for a given world ID.
 * Falls back to floresta-encantada if the world ID is not found.
 */
export function getCollectiblePlacements(worldId: string): CollectiblePlacement[] {
  return WORLD_COLLECTIBLE_PLACEMENTS[worldId] ?? WORLD_COLLECTIBLE_PLACEMENTS['floresta-encantada'];
}
